import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, MaskedLines } from "./Motion";
import { CtaButton, CtaRow, BookMeeting } from "./Cta";

export const Label = ({ n, children, dark }) => (
  <p className={`flex items-center gap-3 font-mono text-[11px] font-medium uppercase tracking-[0.25em] ${dark ? "text-violet-300" : "text-violet-700"}`}>
    {n && <span className={dark ? "text-slate-400" : "text-slate-500"}>({n})</span>}
    <span className={`h-px w-8 ${dark ? "bg-violet-400/50" : "bg-violet-600/40"}`} />
    {children}
  </p>
);

export const H2 = ({ children, dark, className = "" }) => (
  <h2 className={`font-display text-[2.1rem] font-medium leading-[1.02] sm:text-5xl lg:text-[3.6rem] ${dark ? "text-white" : "text-slate-900"} ${className}`}>{children}</h2>
);

export const Paras = ({ items, dark, className = "" }) => (
  <div className={`space-y-4 text-[15px] leading-relaxed sm:text-base ${dark ? "text-slate-300" : "text-slate-600"} ${className}`}>
    {items.map((p, i) => <p key={i}>{p}</p>)}
  </div>
);

export const Bullets = ({ items, dark, cols = 2, testid }) => (
  <ul data-testid={testid} className={`grid gap-x-8 ${cols === 2 ? "sm:grid-cols-2" : cols === 3 ? "sm:grid-cols-2 lg:grid-cols-3" : ""}`}>
    {items.map((b) => (
      <li key={b} className={`group flex items-center gap-3 border-b py-3 text-[14.5px] transition-colors duration-300 ${dark ? "border-white/10 text-slate-200" : "border-slate-200 text-slate-800"}`}>
        <span className={`h-1.5 w-1.5 shrink-0 rotate-45 transition-transform duration-300 group-hover:scale-150 ${dark ? "bg-violet-400" : "bg-violet-600"}`} />
        {b}
      </li>
    ))}
  </ul>
);

export const Section = ({ children, dark, tone, className = "", id, testid }) => {
  const bg = dark ? (tone === "navy" ? "bg-navy" : tone === "plum" ? "bg-plum" : "bg-ink") : tone === "subtle" ? "bg-[#F3F2F8]" : "bg-[#FAFAFC]";
  return (
    <section id={id} data-testid={testid} className={`${dark ? "grain text-white" : ""} ${bg} py-24 sm:py-32 ${className}`}>
      <div className="wrap">{children}</div>
    </section>
  );
};

export const Statement = ({ n, label, title, lead, paras, dark, tone, aside, testid }) => (
  <Section dark={dark} tone={tone} testid={testid}>
    <div className="grid gap-12 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <Reveal><Label n={n} dark={dark}>{label}</Label></Reveal>
        <Reveal delay={0.05}><H2 dark={dark} className="mt-6">{title}</H2></Reveal>
      </div>
      <div className="lg:col-span-6 lg:col-start-7">
        {lead && <Reveal><p className={`font-serif-i text-3xl leading-tight sm:text-4xl ${dark ? "text-violet-200" : "text-violet-800"}`}>{lead}</p></Reveal>}
        <Reveal delay={0.1}><Paras items={paras} dark={dark} className={lead ? "mt-6" : ""} /></Reveal>
        {aside}
      </div>
    </div>
  </Section>
);

export const ServiceBlocks = ({ items, testid }) => (
  <div data-testid={testid} className="mt-16 border-t border-slate-200">
    {items.map((s, i) => (
      <Reveal key={s.title} className="group grid gap-6 border-b border-slate-200 py-12 sm:py-16 lg:grid-cols-12" data-testid={`service-block-${i}`}>
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-28">
            <span className="font-mono text-xs text-slate-500 transition-colors duration-300 group-hover:text-violet-700">{String(i + 1).padStart(2, "0")} /</span>
            <h3 className="mt-3 font-display text-3xl font-medium leading-tight text-slate-900 sm:text-[2.4rem]">{s.title}</h3>
          </div>
        </div>
        <div className="lg:col-span-7 lg:col-start-6">
          <Paras items={s.paras} />
          {s.bullets && <div className="mt-8"><Bullets items={s.bullets} /></div>}
          {s.after && <p className="mt-6 font-serif-i text-2xl text-violet-800">{s.after}</p>}
          {s.link && (
            <Link to={s.link.to} data-testid={`service-link-${i}`} className="mt-8 inline-flex items-center gap-2 font-semibold text-slate-900 underline decoration-violet-500 decoration-2 underline-offset-8 transition-colors hover:text-violet-700">
              {s.link.label} <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
            </Link>
          )}
        </div>
      </Reveal>
    ))}
  </div>
);

export const Process = ({ title, steps, n, testid = "process-steps" }) => (
  <Section dark tone="navy" testid={testid}>
    <div className="max-w-3xl">
      <Reveal><Label n={n} dark>Process</Label></Reveal>
      <Reveal delay={0.05}><H2 dark className="mt-6">{title}</H2></Reveal>
    </div>
    <div className={`mt-16 grid gap-px overflow-hidden rounded-3xl border border-white/10 bg-white/10 sm:grid-cols-2 ${steps.length === 5 ? "lg:grid-cols-5" : "lg:grid-cols-3"}`}>
      {steps.map((s, i) => (
        <Reveal key={s.title} delay={(i % 3) * 0.08} className={`group relative bg-[#0A1233] p-8 transition-colors duration-500 hover:bg-[#121a45] sm:p-10 ${steps.length === 5 && i === 4 ? "sm:col-span-2 lg:col-span-1" : ""}`} data-testid={`process-step-${i}`}>
          <span className="font-display text-6xl font-light text-violet-300/40 transition-colors duration-500 group-hover:text-violet-300 sm:text-7xl">{String(i + 1).padStart(2, "0")}</span>
          <h3 className="mt-8 font-display text-2xl font-medium text-white">{s.title}</h3>
          <p className="mt-3 text-[15px] leading-relaxed text-slate-300">{s.text}</p>
          <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-gradient-to-r from-violet-400 to-indigo-400 transition-[width] duration-700 group-hover:w-full" />
        </Reveal>
      ))}
    </div>
  </Section>
);

export const MetricsCloud = ({ n, label = "Measurement", title, intro, items, outro, testid = "metrics-section" }) => (
  <Section dark testid={testid}>
    <div className="grid gap-12 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <Reveal><Label n={n} dark>{label}</Label></Reveal>
        <Reveal delay={0.05}><H2 dark className="mt-6">{title}</H2></Reveal>
        <Reveal delay={0.1}><Paras items={intro} dark className="mt-6" /></Reveal>
      </div>
      <div className="lg:col-span-7">
        <div className="flex flex-wrap gap-2.5 sm:gap-3">
          {items.map((m, i) => (
            <Reveal key={m} delay={i * 0.04} y={14} className="rounded-full border border-white/15 bg-white/[0.03] px-5 py-3 font-display text-base text-white transition-[background-color,border-color] duration-300 hover:border-violet-400 hover:bg-violet-500/15 sm:text-lg">
              {m}
            </Reveal>
          ))}
        </div>
        {outro && <Reveal delay={0.2}><p className="mt-12 max-w-xl font-serif-i text-2xl leading-snug text-violet-200 sm:text-3xl">{outro}</p></Reveal>}
      </div>
    </div>
  </Section>
);

export const CtaBand = ({ title, paras, sub, ctas, testid = "cta-band" }) => (
  <section data-testid={testid} className="grain relative overflow-hidden bg-plum py-28 text-white sm:py-40">
    <div aria-hidden="true" className="absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full bg-violet-600/30 blur-[120px]" />
    <div aria-hidden="true" className="absolute -bottom-40 -left-20 h-[420px] w-[420px] rounded-full bg-indigo-700/30 blur-[120px]" />
    <div className="wrap">
      <MaskedLines as="h2" inView lines={title} className="max-w-5xl font-display text-[2.4rem] font-medium leading-[0.98] sm:text-6xl lg:text-7xl" />
      <Reveal delay={0.2} className="mt-10 grid gap-10 lg:grid-cols-12">
        <Paras items={paras} dark className="lg:col-span-6" />
        <div className="lg:col-span-5 lg:col-start-8">
          {sub && <p className="mb-6 font-serif-i text-2xl text-violet-200 sm:text-3xl">{sub}</p>}
          <CtaRow>{ctas}</CtaRow>
        </div>
      </Reveal>
    </div>
  </section>
);

export { CtaButton, CtaRow, BookMeeting };
